import React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Film, Clapperboard, Image as ImageIcon, Loader2, Check } from 'lucide-react';

/**
 * Optimize → Media: pick the content being optimized. Covers every asset
 * kind the spec asks for (section 5) using ONLY data that already exists
 * — long-form videos from the project's real Parent Assets, their real
 * `EditorClip`s (fetched on demand via the same `GET
 * /api/editor/projects/source/{id}` endpoint Thumbnails/Clips already
 * use), and Images & Posts designs (images/graphics/carousels/stories)
 * from that same project's `consistency_images_posts_{projectId}`
 * localStorage list — no second Media Library implementation.
 */
export function OptimizeMediaPanel({
  sources, selectedContentRef, onSelectVideo,
  clips, clipsLoading, onSelectClip,
  imageDesigns, onSelectDesign,
}) {
  const isSelected = (kind, id) => selectedContentRef?.kind === kind && selectedContentRef?.id === id;
  const backToWholeVideo = () => {
    const parent = sources.find((s) => s.id === selectedContentRef?.videoSourceId);
    if (parent) onSelectVideo(parent);
  };

  return (
    <div className="space-y-4" data-testid="optimize-media-panel">
      <div>
        <p className="text-[11px] uppercase tracking-wide text-[#555E6E] mb-1.5">Long-form Videos</p>
        {sources.length === 0 ? (
          <p className="text-xs text-[#8A93A6] text-center py-4" data-testid="optimize-media-no-videos">No videos in this project yet.</p>
        ) : (
          <div className="space-y-1.5">
            {sources.map((s) => (
              <button
                key={s.id} onClick={() => onSelectVideo(s)}
                className={`w-full flex items-center gap-2 p-2 rounded-lg border text-left ${isSelected('video', s.id) ? 'border-[#3A7BFF] bg-[#3A7BFF]/10' : 'border-white/10 hover:bg-white/5'}`}
                data-testid={`optimize-media-video-${s.id}`}
              >
                <Film className="h-3.5 w-3.5 text-[#8A93A6] shrink-0" />
                <span className="text-xs text-white truncate flex-1">{s.title || s.id}</span>
                {isSelected('video', s.id) && <Check className="h-3.5 w-3.5 text-[#3A7BFF] shrink-0" />}
              </button>
            ))}
          </div>
        )}
      </div>

      {selectedContentRef?.kind !== 'design' && selectedContentRef?.videoSourceId && (
        <div>
          <p className="text-[11px] uppercase tracking-wide text-[#555E6E] mb-1.5">Clips from this video</p>
          {clipsLoading ? (
            <p className="text-xs text-[#8A93A6] flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" />Loading…</p>
          ) : clips.length === 0 ? (
            <p className="text-xs text-[#8A93A6] text-center py-2" data-testid="optimize-media-no-clips">No clips generated from this video yet.</p>
          ) : (
            <Select value={selectedContentRef?.kind === 'clip' ? selectedContentRef.id : '__video__'} onValueChange={(v) => (v === '__video__' ? backToWholeVideo() : onSelectClip(clips.find((c) => c.id === v)))}>
              <SelectTrigger className="h-8 text-xs bg-white/5 border-white/10" data-testid="optimize-media-clip-select"><SelectValue placeholder="Optimize a specific clip instead" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__video__">Whole long-form video</SelectItem>
                {clips.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({c.output_type?.toUpperCase()})</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
      )}

      <div>
        <p className="text-[11px] uppercase tracking-wide text-[#555E6E] mb-1.5">Images &amp; Posts (images, carousels, Stories)</p>
        {imageDesigns.length === 0 ? (
          <p className="text-xs text-[#8A93A6] text-center py-4" data-testid="optimize-media-no-designs">No image/post designs in this project yet — create one in Images &amp; Posts.</p>
        ) : (
          <div className="space-y-1.5">
            {imageDesigns.map((d) => (
              <button
                key={d.id} onClick={() => onSelectDesign(d)}
                className={`w-full flex items-center gap-2 p-2 rounded-lg border text-left ${isSelected('design', d.id) ? 'border-[#3A7BFF] bg-[#3A7BFF]/10' : 'border-white/10 hover:bg-white/5'}`}
                data-testid={`optimize-media-design-${d.id}`}
              >
                {d.assetType?.includes('story') ? <Clapperboard className="h-3.5 w-3.5 text-[#8A93A6] shrink-0" /> : <ImageIcon className="h-3.5 w-3.5 text-[#8A93A6] shrink-0" />}
                <span className="text-xs text-white truncate flex-1">{d.name}</span>
                {isSelected('design', d.id) && <Check className="h-3.5 w-3.5 text-[#3A7BFF] shrink-0" />}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
